import React, { useEffect, useState, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Menu, X, Send } from 'lucide-react';
interface GBIntelligencePageProps {
  onNavigate: (page: string) => void;
}
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isStreaming?: boolean;
}
interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  lastMessage: string;
  timestamp: Date;
}
// Mock responses for quick questions
const MOCK_RESPONSES: Record<string, string> = {
  'Come viene calcolato il mio impatto settimanale?': "Il tuo impatto settimanale è una stima basata sulle azioni sostenibili che completi e sulle tue abitudini registrate nell'app. Utilizziamo fattori medi di riduzione delle emissioni per ogni tipo di azione (es. andare in bici invece che in auto, riciclare, usare prodotti riutilizzabili). Il calcolo viene aggiornato in tempo reale ogni volta che registri una nuova azione. Più azioni completi, più alto sarà il tuo punteggio di impatto settimanale!",
  'Quali dati dimostrano il cambiamento climatico?': "I principali indicatori del cambiamento climatico includono: l'aumento della concentrazione di CO₂ nell'atmosfera (da 280 ppm pre-industriale a oltre 420 ppm oggi), l'incremento delle temperature globali medie (+1.1°C dal 1880), lo scioglimento accelerato dei ghiacci polari e dei ghiacciai, l'innalzamento del livello del mare (circa 20cm dal 1900), e l'aumento della frequenza di eventi meteorologici estremi. Questi dati sono raccolti da migliaia di stazioni di monitoraggio in tutto il mondo e confermati da decenni di ricerca scientifica.",
  'Come funzionano i punti su Greenback?': 'Su Greenback guadagni punti (Diamond e Stelle) completando azioni sostenibili e challenge. I punti Diamond hanno valore monetario e possono essere convertiti in buoni acquisto o denaro. I punti Stelle hanno valore non monetario e ti permettono di sbloccare funzionalità premium, accedere a eventi esclusivi e contenuti riservati. Più azioni certificate completi, più punti accumuli. Puoi vedere il tuo saldo in alto a destra e usare i punti nella sezione Premi!'
};
const DEFAULT_RESPONSE = "Ecco alcune azioni sostenibili che puoi fare ogni settimana:\n\n• Usa i mezzi pubblici o la bici invece dell'auto (2-3 volte/settimana)\n• Riduci il consumo di carne (almeno 2 pasti vegetariani)\n• Porta sempre con te una borraccia e una borsa riutilizzabile\n• Separa correttamente i rifiuti per il riciclo\n• Spegni le luci e gli elettrodomestici quando non li usi\n• Fai acquisti consapevoli: preferisci prodotti locali e con meno imballaggi\n• Riduci gli sprechi alimentari pianificando i pasti\n\nOgni piccola azione conta! Registrale su Greenback per tracciare il tuo impatto.";
export function GBIntelligencePage({
  onNavigate
}: GBIntelligencePageProps) {
  const [conversations, setConversations] = useState<Conversation[]>([{
    id: '1',
    title: 'Impatto settimanale',
    lastMessage: 'Come viene calcolato il mio impatto...',
    timestamp: new Date(Date.now() - 86400000),
    messages: [{
      id: '1-1',
      text: 'Come viene calcolato il mio impatto settimanale?',
      sender: 'user',
      timestamp: new Date(Date.now() - 86400000)
    }, {
      id: '1-2',
      text: MOCK_RESPONSES['Come viene calcolato il mio impatto settimanale?'],
      sender: 'bot',
      timestamp: new Date(Date.now() - 86400000 + 5000)
    }]
  }, {
    id: '2',
    title: 'Crediti Diamond',
    lastMessage: 'Come funzionano i punti su Greenback?',
    timestamp: new Date(Date.now() - 172800000),
    messages: [{
      id: '2-1',
      text: 'Come funzionano i punti su Greenback?',
      sender: 'user',
      timestamp: new Date(Date.now() - 172800000)
    }, {
      id: '2-2',
      text: MOCK_RESPONSES['Come funzionano i punti su Greenback?'],
      sender: 'bot',
      timestamp: new Date(Date.now() - 172800000 + 5000)
    }]
  }, {
    id: '3',
    title: 'Cambiamento climatico',
    lastMessage: 'Quali dati dimostrano il cambiamento...',
    timestamp: new Date(Date.now() - 259200000),
    messages: [{
      id: '3-1',
      text: 'Quali dati dimostrano il cambiamento climatico?',
      sender: 'user',
      timestamp: new Date(Date.now() - 259200000)
    }, {
      id: '3-2',
      text: MOCK_RESPONSES['Quali dati dimostrano il cambiamento climatico?'],
      sender: 'bot',
      timestamp: new Date(Date.now() - 259200000 + 5000)
    }]
  }]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const streamingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const quickQuestions = ['Come viene calcolato il mio impatto settimanale?', 'Quali dati dimostrano il cambiamento climatico?', 'Come funzionano i punti su Greenback?'];
  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        setTimeout(() => {
          viewport.scrollTop = viewport.scrollHeight;
        }, 50);
      }
    }
  }, [messages]);
  // Handle mobile keyboard - scroll input into view when focused
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
        if (inputContainerRef.current) {
          inputContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          });
        }
      }, 300);
    };
    input.addEventListener('focus', handleFocus);
    return () => input.removeEventListener('focus', handleFocus);
  }, []);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);
  const streamResponse = (responseText: string) => {
    let currentIndex = 0;
    const botMessageId = Date.now().toString();
    const botMessage: Message = {
      id: botMessageId,
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      isStreaming: true
    };
    setMessages(prev => [...prev, botMessage]);
    streamingIntervalRef.current = setInterval(() => {
      if (currentIndex < responseText.length) {
        const char = responseText[currentIndex];
        setMessages(prev => prev.map(msg => msg.id === botMessageId ? {
          ...msg,
          text: msg.text + char
        } : msg));
        currentIndex++;
      } else {
        if (streamingIntervalRef.current) {
          clearInterval(streamingIntervalRef.current);
          streamingIntervalRef.current = null;
        }
        setMessages(prev => prev.map(msg => msg.id === botMessageId ? {
          ...msg,
          isStreaming: false
        } : msg));
        setIsStreaming(false);
      }
    }, 20);
  };
  const sendMessage = (text?: string) => {
    const messageText = text || inputText.trim();
    if (!messageText || isTyping || isStreaming) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      setIsStreaming(true);
      const response = MOCK_RESPONSES[messageText] || DEFAULT_RESPONSE;
      streamResponse(response);
    }, 4000);
  };
  const handleQuickQuestion = (question: string) => {
    sendMessage(question);
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  const loadConversation = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setCurrentConversationId(conversationId);
      setMessages(conversation.messages);
      setShowDrawer(false);
    }
  };
  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setShowDrawer(false);
  };
  const isInputDisabled = isTyping || isStreaming;
  if (isLoading) {
    return <div className="flex flex-col items-center justify-center h-screen bg-gray-50" data-id="element-442">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 p-[2px] animate-pulse" style={{
        background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
      }} data-id="element-443">
          <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-3" data-id="element-444">
            <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-10 h-10 object-contain" data-id="element-445" />
          </div>
        </div>
        <p className="text-sm text-gray-500" data-id="element-446">Caricamento...</p>
      </div>;
  }
  return <>
      {/* Fixed Header - Completely independent from main container */}
      <header className="fixed top-0 left-0 right-0 px-6 py-4 bg-white border-b border-gray-100 z-50" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0
    }} data-id="element-447">
        <div className="flex items-center justify-between max-w-md mx-auto" data-id="element-448">
          <button onClick={() => setShowDrawer(true)} className="text-gray-900 hover:text-green-600 transition-colors" data-id="element-449">
            <Menu className="h-6 w-6" strokeWidth={1.5} data-id="element-450" />
          </button>
          <h1 className="text-lg font-normal text-gray-900" data-id="element-451">GB Intelligence</h1>
          <button onClick={() => onNavigate('home')} className="text-gray-900 hover:text-green-600 transition-colors" data-id="element-452">
            <X className="h-6 w-6" strokeWidth={1.5} data-id="element-453" />
          </button>
        </div>
      </header>

      {/* Main Content Container */}
      <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900 animate-fade-in" data-id="element-454">
        {/* Chat Messages */}
        <ScrollArea className="flex-1 pt-20 pb-24" ref={scrollAreaRef} data-id="element-455">
          <div className="px-6 py-6 space-y-6 max-w-md mx-auto" data-id="element-456">
            {/* Hero Area - Only show when no messages */}
            {messages.length === 0 && <div className="flex flex-col items-center space-y-6 pt-8" data-id="element-457">
                {/* Greenback Logo Avatar with gradient border */}
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 p-[2px]" style={{
              background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
            }} data-id="element-458">
                  <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-3" data-id="element-459">
                    <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-10 h-10 object-contain" data-id="element-460" />
                  </div>
                </div>

                {/* Quick Questions */}
                <div className="w-full space-y-3" data-id="element-461">
                  <p className="text-sm text-gray-500 text-center mb-4" data-id="element-462">
                    Inizia con una domanda:
                  </p>
                  {quickQuestions.map((question, index) => <button key={index} onClick={() => handleQuickQuestion(question)} disabled={isInputDisabled} className="w-full text-left px-4 py-3 rounded-xl bg-white border border-gray-200 hover:border-green-500 hover:shadow-md transition-all text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-463">
                      {question}
                    </button>)}
                </div>
              </div>}

            {/* Messages */}
            {messages.map(message => <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} gap-2`} data-id="element-464">
                {/* AI Avatar with logo */}
                {message.sender === 'bot' && <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 p-[2px]" style={{
              background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
            }} data-id="element-465">
                    <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-1" data-id="element-466">
                      <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="GB" className="w-4 h-4 object-contain" data-id="element-467" />
                    </div>
                  </div>}

                <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${message.sender === 'user' ? 'bg-green-600 text-white' : 'bg-white border border-gray-200 text-gray-900'}`} data-id="element-468">
                  <p className="text-sm leading-relaxed whitespace-pre-line" data-id="element-469">
                    {message.text}
                    {message.isStreaming && <span className="inline-block w-1 h-4 bg-green-600 ml-0.5 animate-pulse" data-id="element-470" />}
                  </p>
                  {!message.isStreaming && <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-green-100' : 'text-gray-400'}`} data-id="element-471">
                      {message.timestamp.toLocaleTimeString('it-IT', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </p>}
                </div>
              </div>)}

            {/* Typing Indicator */}
            {isTyping && <div className="flex justify-start gap-2" data-id="element-472">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 p-[2px]" style={{
              background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
            }} data-id="element-473">
                  <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-1" data-id="element-474">
                    <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="GB" className="w-4 h-4 object-contain" data-id="element-475" />
                  </div>
                </div>
                <div className="max-w-[75%] rounded-2xl px-4 py-3 bg-white border border-gray-200" data-id="element-476">
                  <div className="flex items-center gap-1" data-id="element-477">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-478" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0.2s'
                }} data-id="element-479" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0.4s'
                }} data-id="element-480" />
                  </div>
                </div>
              </div>}
          </div>
        </ScrollArea>

        {/* Fixed Input Area - Mobile keyboard friendly */}
        <div ref={inputContainerRef} className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 py-4 z-40" style={{
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))'
      }} data-id="element-481">
          <div className="max-w-md mx-auto flex items-center gap-3" data-id="element-482">
            <input ref={inputRef} type="text" value={inputText} onChange={e => setInputText(e.target.value)} onKeyPress={handleKeyPress} disabled={isInputDisabled} placeholder={isInputDisabled ? 'Attendi la risposta...' : 'Scrivi un messaggio...'} className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-483" />
            <button onClick={() => sendMessage()} disabled={!inputText.trim() || isInputDisabled} className="w-12 h-12 rounded-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center flex-shrink-0" data-id="element-484">
              <Send className="h-5 w-5 text-white" strokeWidth={2} style={{
              transform: 'translateX(1px)'
            }} data-id="element-485" />
            </button>
          </div>
        </div>
      </div>

      {/* Conversations Drawer */}
      {showDrawer && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => setShowDrawer(false)} data-id="element-486" />

          <div className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 shadow-xl animate-slide-in-left" data-id="element-487">
            <div className="flex flex-col h-full" data-id="element-488">
              <div className="px-6 py-4 border-b border-gray-100" data-id="element-489">
                <div className="flex items-center justify-between" data-id="element-490">
                  <h2 className="text-lg font-normal text-gray-900" data-id="element-491">
                    Conversazioni
                  </h2>
                  <button onClick={() => setShowDrawer(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-492">
                    <X className="h-5 w-5" strokeWidth={1.5} data-id="element-493" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto" data-id="element-494">
                <div className="p-4 space-y-2" data-id="element-495">
                  <button onClick={startNewConversation} className="w-full px-4 py-3 rounded-xl bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-normal" data-id="element-496">
                    + Nuova conversazione
                  </button>

                  {conversations.map(conv => <button key={conv.id} onClick={() => loadConversation(conv.id)} className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${currentConversationId === conv.id ? 'bg-green-50 border border-green-200' : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-497">
                      <p className="text-sm font-normal text-gray-900 mb-1" data-id="element-498">
                        {conv.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate" data-id="element-499">
                        {conv.lastMessage}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-500">
                        {conv.timestamp.toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short'
                  })}
                      </p>
                    </button>)}
                </div>
              </div>
            </div>
          </div>
        </>}
    </>;
}